import { motion } from "framer-motion";
import { Heart, Users, Building2, Handshake, ArrowUpRight } from "lucide-react";

export default function GetInvolved({ onDonate }) {
  const cards = [
    {
      icon: Heart,
      title: "Donate",
      desc: "Fuel daily programming — nutritional snacks, supplies, mentors, nutritional guidance and counseling.",
      cta: "Give now",
      action: "donate",
      bg: "bg-brand-coral text-white",
      span: "md:col-span-7",
    },
    {
      icon: Users,
      title: "Volunteer",
      desc: (
        <ul className="space-y-1.5 list-none">
          <li className="flex gap-2">
            <span className="text-brand-coral font-black mt-0.5">✓</span>
            <span>Earn verified volunteer hours — great for resumes, internships &amp; school requirements.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-brand-coral font-black mt-0.5">✓</span>
            <span>Gain hands-on experience in community nutrition &amp; wellness.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-brand-coral font-black mt-0.5">✓</span>
            <span>Make a real impact while building skills that stand out.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-brand-coral font-black mt-0.5">✓</span>
            <span>Flexible opportunities for students &amp; professionals.</span>
          </li>
        </ul>
      ),
      cta: "Reach out",
      action: "contact",
      bg: "bg-white text-brand-navy",
      span: "md:col-span-5",
    },
    {
      icon: Building2,
      title: "Sponsor a Cohort",
      desc: "Fund a full group of teens for a quarter — branded impact.",
      cta: "Let's talk",
      action: "contact",
      bg: "bg-brand-navy text-white",
      span: "md:col-span-5",
    },
    {
      icon: Handshake,
      title: "Partner",
      desc: "Schools, trades, businesses — connect youth to pathways.",
      cta: "Partner with us",
      action: "contact",
      bg: "bg-brand-yellow text-brand-navy",
      span: "md:col-span-7",
    },
  ];

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="involved"
      data-testid="get-involved-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-coral mb-4">
            Get involved
          </div>
          <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-brand-navy">
            There&apos;s a door for everyone.{" "}
            <span className="text-slate-400">Pick yours.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.button
                key={c.title}
                data-testid={`involved-card-${c.title.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => (c.action === "donate" ? onDonate() : scrollToContact())}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`col-span-1 ${c.span} ${c.bg} rounded-3xl p-8 md:p-10 text-left group hover:-translate-y-1 transition-transform duration-300 border border-black/5 min-h-[240px] flex flex-col justify-between`}
              >
                <div className="flex items-start justify-between">
                  <div className="w-14 h-14 rounded-2xl bg-black/10 flex items-center justify-center">
                    <Icon className="w-7 h-7" />
                  </div>
                  <ArrowUpRight className="w-7 h-7 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
                </div>
                <div className="mt-8">
                  <h3 className="font-display font-black text-3xl md:text-4xl leading-tight">
                    {c.title}
                  </h3>
                  <div className="mt-3 text-base leading-relaxed opacity-90 max-w-md">
                    {c.desc}
                  </div>
                  <div className="mt-5 inline-flex items-center gap-2 font-bold text-sm uppercase tracking-wider">
                    {c.cta}
                    <span className="w-8 h-px bg-current" />
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
